/**
 * Shared value-label mappings for enum-backed cube dimensions.
 *
 * Cube dimensions backed by Postgres enums or string literal columns store
 * machine-friendly identifiers (e.g. `non_voting`). Reports surface these
 * dimensions in tables, charts, group headings, and exports — anywhere a
 * raw value would otherwise reach end users. Attaching one of these maps to
 * the dimension's `valueLabels` keeps display labels in a single place.
 */
/** `MeetingSeatType` enum (`packages/db-app/prisma/models/meeting_body_seats.prisma`). */
export const seatTypeValueLabels = {
    voting: 'Voting',
    non_voting: 'Non-Voting',
    ex_officio: 'Ex-Officio',
};
/** `MeetingSeatTermStatus` enum (`packages/db-app/prisma/models/meeting_body_seats.prisma`). */
export const seatTermStatusValueLabels = {
    vacant: 'Vacant',
    nominated: 'Nominated',
    active: 'Active',
};
/** `MeetingBodyType` enum (`packages/db-app/prisma/models/meeting_bodies.prisma`). */
export const meetingBodyTypeValueLabels = {
    primary: 'Primary',
    committee: 'Committee',
    board: 'Board',
};
/** `MeetingStatus` enum (`packages/db-app/prisma/models/meeting_events.prisma`). */
export const meetingStatusValueLabels = {
    upcoming: 'Upcoming',
    live: 'Live',
    completed: 'Completed',
    cancelled: 'Cancelled',
};
/** `MinutesStatus` enum (`packages/db-app/prisma/models/meeting_minutes.prisma`). */
export const minutesStatusValueLabels = {
    draft: 'Draft',
    scheduled: 'Scheduled',
    approved: 'Approved',
};
/** `LegislativeItemSystemStatus` enum (`packages/db-app/prisma/models/legislative_items.prisma`). */
export const legislativeItemSystemStatusValueLabels = {
    draft: 'Draft',
    review: 'Review',
    deliberation: 'Deliberation',
    processing: 'Processing',
    closed_rejected: 'Closed (Rejected)',
    closed_approved: 'Closed (Approved)',
    closed_cancelled: 'Closed (Cancelled)',
};
/** `LegislativeTypeStatus` enum (`packages/db-app/prisma/models/legislative_types.prisma`). */
export const legislativeTypeStatusValueLabels = {
    active: 'Active',
    inactive: 'Inactive',
};
/** `LegislativeItemWorkflowTemplateStatus` enum (`packages/db-app/prisma/models/legislative_item_workflows.prisma`). */
export const workflowTemplateStatusValueLabels = {
    draft: 'Draft',
    published: 'Published',
    archived: 'Archived',
};
/** `SeatCampaignStatus` enum (`packages/db-app/prisma/models/seat_campaigns.prisma`). */
export const seatCampaignStatusValueLabels = {
    draft: 'Draft',
    active: 'Active',
    completed: 'Completed',
};
/** `SeatApplicationStatus` enum from `packages/validation/src/seat-applications/index.ts`. */
export const seatApplicationStatusValueLabels = {
    draft: 'Draft',
    submitted: 'Submitted',
};
/** `MeetingBodySeatTermApplicationStatus` enum from `packages/validation/src/seat-applications/index.ts`. */
export const seatTermApplicationStatusValueLabels = {
    draft: 'Draft',
    pending: 'Pending',
    review: 'In Review',
    nominated: 'Nominated',
    confirmed: 'Confirmed',
    declined: 'Declined',
    withdrawn: 'Withdrawn',
    archived: 'Archived',
};
/** `MeetingEventDocumentType` enum (`packages/db-app/prisma/models/meeting_events_documents.prisma`). */
export const meetingEventDocumentTypeValueLabels = {
    other: 'Other',
    agenda_outline: 'Agenda Outline',
    agenda_package: 'Agenda Package',
    minutes: 'Minutes',
    transcript: 'Transcript',
    public_notice: 'Public Notice',
    public_comments: 'Public Comments',
};
/** `SeatApplicationWorkflowStatus` enum from `packages/validation/src/seat-application-workflows/index.ts`. */
export const seatApplicationWorkflowStatusValueLabels = {
    draft: 'Draft',
    published: 'Published',
    archived: 'Archived',
};
/** `SeatApplicationTemplateStatus` enum from `packages/validation/src/seat-application-templates/index.ts`. */
export const seatApplicationTemplateStatusValueLabels = {
    draft: 'Draft',
    published: 'Published',
    archived: 'Archived',
};
/** `GeneratedDocumentsStatus` enum (`packages/db-app/prisma/models/legislative_items_agenda_documents.prisma`). */
export const generatedDocumentsStatusValueLabels = {
    pending: 'Pending',
    failed: 'Failed',
    completed: 'Completed',
};
/** `SeatApplicationWorkflowStepType` enum from `packages/validation/src/seat-application-workflows/index.ts`. */
export const seatApplicationWorkflowStepTypeValueLabels = {
    meeting_body: 'Meeting Body',
    meeting_body_role: 'Meeting Body Role',
    position: 'Position',
    users: 'Users',
};
/** `VoteType` enum (`packages/db-app/prisma/models/minutes_motions.prisma`). */
export const voteTypeValueLabels = {
    aye: 'Aye',
    nay: 'Nay',
    abstain: 'Abstain',
    recuse: 'Recuse',
    absent: 'Absent',
};
/** `VoteOutcome` from `packages/validation/src/meeting-events/minutes/vote-outcome.ts`. */
export const voteOutcomeValueLabels = {
    pass: 'Passed',
    fail: 'Failed',
};
/** `attendance_records.status` dimension (`apps/cube/model/cubes/attendance_records.js`). */
export const attendanceStatusValueLabels = {
    present: 'Present',
    excused: 'Excused',
    absent: 'Absent',
};
/** `attendance_records.attendance_mode` dimension (`apps/cube/model/cubes/attendance_records.js`). */
export const attendanceModeValueLabels = {
    in_person: 'In Person',
    remote: 'Remote',
};
